import tinycolor from "tinycolor2";

/** Converts a color to "r g b" string format */
const toRGBString = (color: tinycolor.Instance): string => {
    const { r, g, b } = color.toRgb();
    return `${r} ${g} ${b}`;
};

/** Type definition for a generated theme */
export type Theme = Record<string, string>;

/**
 * Manual color mixing since tinycolor2 doesn't have mix method
 */
const mixColors = (color1: tinycolor.Instance, color2: tinycolor.Instance, weight: number): tinycolor.Instance => {
    const rgb1 = color1.toRgb();
    const rgb2 = color2.toRgb();

    const r = Math.round(rgb1.r * (1 - weight) + rgb2.r * weight);
    const g = Math.round(rgb1.g * (1 - weight) + rgb2.g * weight);
    const b = Math.round(rgb1.b * (1 - weight) + rgb2.b * weight);

    return tinycolor({ r, g, b });
};

/**
 * Generates a complete, harmonious theme based on any base color
 * @param baseColor - Base hex color (e.g. "#a01a7d", "#255F38", "#3b82f6")
 * @returns Complete theme object with CSS variables
 */
export function generateTheme(baseColor: string): Theme {
    const color = tinycolor(baseColor);
    const hsl = color.toHsl();
    const hue = hsl.h;
    const saturation = hsl.s;
    const lightness = hsl.l;

    // Determine color characteristics
    const isWarm = (hue >= 0 && hue <= 60) || (hue >= 330 && hue <= 360); // Reds, oranges, yellows
    const isCool = hue >= 150 && hue <= 270; // Greens, blues
    const isNeutral = (hue > 60 && hue < 150) || (hue > 270 && hue < 330); // Yellows->greens, purples

    // Generate harmonious color palette
    const generateHarmoniousColors = () => {
        const baseHue = hue;

        // Analogous colors (adjacent on color wheel)
        const analogous1 = tinycolor({ h: (baseHue + 30) % 360, s: saturation, l: lightness });
        const analogous2 = tinycolor({ h: (baseHue - 30 + 360) % 360, s: saturation, l: lightness });

        // Complementary color (opposite on color wheel)
        const complementary = tinycolor({ h: (baseHue + 180) % 360, s: Math.min(saturation + 0.1, 1), l: lightness });

        // Triadic colors (equidistant on color wheel)
        const triadic1 = tinycolor({ h: (baseHue + 120) % 360, s: saturation, l: lightness });
        const triadic2 = tinycolor({ h: (baseHue + 240) % 360, s: saturation, l: lightness });

        return {
            primary: color,
            secondary: isWarm ? analogous1 : complementary,
            accent: isCool ? triadic1 : analogous2,
            success: tinycolor({ h: 142, s: 0.76, l: 0.36 }), // Consistent success green
            warning: tinycolor({ h: 38, s: 0.92, l: 0.50 }), // Consistent warning orange
            danger: tinycolor({ h: 0, s: 0.84, l: 0.56 }),   // Consistent danger red
            info: tinycolor({ h: 221, s: 0.83, l: 0.53 })    // Consistent info blue
        };
    };

    const palette = generateHarmoniousColors();
    const shades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

    // Helper to generate shade scales
    const generateShadeScale = (baseColor: tinycolor.Instance, lightSaturation: number, darkSaturation: number) => {
        return Object.fromEntries(
            shades.map((shade, index) => {
                let adjustedColor = baseColor.clone();

                if (index < 5) {
                    // Light shades - increase lightness, adjust saturation
                    const lightAmount = (5 - index) * 12;
                    adjustedColor = adjustedColor.lighten(lightAmount);
                    if (lightSaturation !== 1) {
                        const satAdjustment = (1 - lightSaturation) * (index * 0.1);
                        adjustedColor = adjustedColor.saturate(satAdjustment);
                    }
                } else {
                    // Dark shades - decrease lightness, adjust saturation
                    const darkAmount = (index - 4) * 8;
                    adjustedColor = adjustedColor.darken(darkAmount);
                    if (darkSaturation !== 1) {
                        const satAdjustment = (1 - darkSaturation) * ((9 - index) * 0.1);
                        adjustedColor = adjustedColor.saturate(satAdjustment);
                    }
                }

                return [`--color-primary-${shade}`, toRGBString(adjustedColor)];
            })
        );
    };

    const theme: Theme = {
        // ===== TEXT COLORS =====
        // High contrast for readability
        "--color-text-primary": toRGBString(tinycolor("#0f172a")),    // Slate-900
        "--color-text-secondary": toRGBString(tinycolor("#334155")),  // Slate-700
        "--color-text-tertiary": toRGBString(tinycolor("#64748b")),   // Slate-500
        "--color-text-muted": toRGBString(tinycolor("#94a3b8")),      // Slate-400
        "--color-text-danger": toRGBString(palette.danger.darken(20)),
        "--color-text-success": toRGBString(palette.success.darken(20)),
        "--color-text-warning": toRGBString(tinycolor("#92400e")),    // Amber-900
        "--color-text-info": toRGBString(palette.info.darken(20)),

        // ===== PRIMARY COLOR SCALE =====
        ...generateShadeScale(palette.primary, 0.9, 1.1),

        // ===== SECONDARY COLOR SCALE =====
        ...Object.fromEntries(
            shades.map((shade, index) => [
                `--color-secondary-${shade}`,
                toRGBString(
                    index < 5
                        ? palette.secondary.clone().lighten((5 - index) * 10).saturate(0.1)
                        : palette.secondary.clone().darken((index - 4) * 9).desaturate(0.1)
                )
            ])
        ),

        // ===== ACCENT COLOR SCALE =====
        ...Object.fromEntries(
            shades.map((shade, index) => [
                `--color-accent-${shade}`,
                toRGBString(
                    index < 5
                        ? palette.accent.clone().lighten((5 - index) * 11).saturate(0.05)
                        : palette.accent.clone().darken((index - 4) * 8).desaturate(0.05)
                )
            ])
        ),

        // ===== NEUTRAL BACKGROUND SCALES =====
        // Background scale - light grays with subtle tint
        ...Object.fromEntries(
            shades.map((shade, index) => [
                `--color-background-${shade}`,
                toRGBString(
                    (() => {
                        if (index < 6) {
                            const baseGray = tinycolor("#ffffff").darken(index * 1.2);
                            return mixColors(baseGray, palette.primary, index * 0.02);
                        } else {
                            const baseDark = tinycolor("#0f172a").lighten((9 - index) * 4);
                            return mixColors(baseDark, palette.primary, (9 - index) * 0.03);
                        }
                    })()
                )
            ])
        ),

        // Card scale - pure with subtle tint
        ...Object.fromEntries(
            shades.map((shade, index) => [
                `--color-card-${shade}`,
                toRGBString(
                    (() => {
                        if (index < 5) {
                            const baseWhite = tinycolor("#ffffff").darken(index * 1);
                            return mixColors(baseWhite, palette.primary, index * 0.01);
                        } else {
                            const baseLight = tinycolor("#f8fafc").darken((index - 4) * 12);
                            return mixColors(baseLight, palette.primary, (index - 4) * 0.02);
                        }
                    })()
                )
            ])
        ),

        // ===== SEMANTIC COLOR SCALES =====
        // Success scale
        ...Object.fromEntries(
            shades.map((shade, index) => [
                `--color-success-${shade}`,
                toRGBString(
                    index < 5
                        ? palette.success.clone().lighten((5 - index) * 9)
                        : palette.success.clone().darken((index - 4) * 8)
                )
            ])
        ),

        // Warning scale
        ...Object.fromEntries(
            shades.map((shade, index) => [
                `--color-warning-${shade}`,
                toRGBString(
                    index < 5
                        ? palette.warning.clone().lighten((5 - index) * 8)
                        : palette.warning.clone().darken((index - 4) * 7)
                )
            ])
        ),

        // Danger scale
        ...Object.fromEntries(
            shades.map((shade, index) => [
                `--color-danger-${shade}`,
                toRGBString(
                    index < 5
                        ? palette.danger.clone().lighten((5 - index) * 10)
                        : palette.danger.clone().darken((index - 4) * 9)
                )
            ])
        ),

        // Info scale
        ...Object.fromEntries(
            shades.map((shade, index) => [
                `--color-info-${shade}`,
                toRGBString(
                    index < 5
                        ? palette.info.clone().lighten((5 - index) * 9)
                        : palette.info.clone().darken((index - 4) * 8)
                )
            ])
        ),

        // ===== SIDEBAR SPECIFIC =====
        // Sidebar uses desaturated primary with good contrast
        ...Object.fromEntries(
            shades.map((shade, index) => [
                `--color-sidebar-${shade}`,
                toRGBString(
                    index < 5
                        ? palette.primary.clone().desaturate(25).lighten((5 - index) * 11)
                        : palette.primary.clone().desaturate(30).darken((index - 4) * 7)
                )
            ])
        ),

        "--color-sidebar-link-500": toRGBString(palette.primary),
        "--color-sidebar-text": toRGBString(tinycolor("#0f172a")),
        "--color-sidebar-text-muted": toRGBString(tinycolor("#475569")),

        // ===== BORDER COLORS =====
        "--color-border": toRGBString(tinycolor("#e2e8f0")),
        "--color-border-strong": toRGBString(tinycolor("#cbd5e1")),
        "--color-border-weak": toRGBString(tinycolor("#f1f5f9")),

        // ===== LINK COLORS =====
        "--color-link": toRGBString(palette.primary),
        "--color-link-hover": toRGBString(palette.primary.darken(15)),
        "--color-link-visited": toRGBString(palette.secondary),
    };

    return theme;
}

/**
 * Applies the generated theme to the document root
 * @param theme - The generated theme object
 */
export function applyTheme(theme: Theme): void {
    const root = document.documentElement;
    Object.entries(theme).forEach(([key, value]) => {
        console.log(`Setting ${key} to ${value}`);
        root.style.setProperty(key, value);
    });
}
